// Klines-Analyse (ICT/SMC-Math). 1:1 portiert aus V1 useDataSync.js:
// detectSwings, calcTrend, calcFib, detectOBs, detectFVGs, yahooCandlesToBinance, analyzeKlines.
// Pure functions — kein React, kein localStorage, kein fetch.

import type {
  FairValueGap,
  FibLevels,
  KlineRow,
  KlinesAnalysis,
  OrderBlock,
  SwingPoint,
  Trend,
} from '@/types/market';

const num = (v: unknown): number => {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') return parseFloat(v);
  return Number.NaN;
};

/**
 * Erkennt Swing-Highs / Swing-Lows mit `n` Bestätigungskerzen links + rechts.
 * V1: n=3, max 5 letzte zurückgeben.
 */
export function detectSwings(
  candles: KlineRow[],
  n = 3,
): { highs: SwingPoint[]; lows: SwingPoint[] } {
  const highs: SwingPoint[] = [];
  const lows: SwingPoint[] = [];
  for (let i = n; i < candles.length - n; i++) {
    const c = candles[i]!;
    const h = num(c[2]);
    const l = num(c[3]);
    let isHigh = true;
    let isLow = true;
    for (let j = i - n; j <= i + n; j++) {
      if (j === i) continue;
      const cj = candles[j]!;
      if (num(cj[2]) >= h) isHigh = false;
      if (num(cj[3]) <= l) isLow = false;
    }
    if (isHigh) highs.push({ price: h, time: c[0]! });
    if (isLow) lows.push({ price: l, time: c[0]! });
  }
  return { highs: highs.slice(-5), lows: lows.slice(-5) };
}

/**
 * Klassifiziert den Trend nach den letzten beiden Swings:
 * - HH + HL → BULLISH
 * - LH + LL → BEARISH
 * - sonst → RANGING
 * - <2 Highs oder <2 Lows → UNCLEAR
 */
export function calcTrend(swings: { highs: SwingPoint[]; lows: SwingPoint[] }): Trend {
  const h = swings.highs;
  const l = swings.lows;
  if (h.length < 2 || l.length < 2) return 'UNCLEAR';
  const hh = h[h.length - 1]!.price > h[h.length - 2]!.price;
  const hl = l[l.length - 1]!.price > l[l.length - 2]!.price;
  const lh = h[h.length - 1]!.price < h[h.length - 2]!.price;
  const ll = l[l.length - 1]!.price < l[l.length - 2]!.price;
  if (hh && hl) return 'BULLISH';
  if (lh && ll) return 'BEARISH';
  return 'RANGING';
}

/**
 * Fibonacci-Retracements zwischen Swing-Low und Swing-High. V1-Konvention:
 * `p0` = swingHigh, `p100` = swingLow. Extensions `ext1272`/`ext1618`.
 * Alle Werte auf 2 Nachkommastellen gerundet.
 */
export function calcFib(swingLow: number, swingHigh: number): FibLevels {
  const range = swingHigh - swingLow;
  const f = (pct: number): number => parseFloat((swingHigh - range * pct).toFixed(2));
  return {
    p0: f(0),
    p236: f(0.236),
    p382: f(0.382),
    p500: f(0.5),
    p618: f(0.618),
    p705: f(0.705),
    p786: f(0.786),
    p100: f(1.0),
    ext1272: parseFloat((swingLow - range * 0.272).toFixed(2)),
    ext1618: parseFloat((swingLow - range * 0.618).toFixed(2)),
  };
}

/**
 * Erkennt Order-Blocks (letzte gegensätzliche Kerze vor Impulse-Move).
 * Filter: nextBody > 1.5× avgBody der letzten 20 Kerzen. Max 4 letzte zurückgeben.
 */
export function detectOBs(candles: KlineRow[]): OrderBlock[] {
  const obs: OrderBlock[] = [];
  const bodies = candles.map((c) => Math.abs(num(c[4]) - num(c[1])));
  const last20 = bodies.slice(-20);
  const avgBody =
    last20.length > 0 ? last20.reduce((s, b) => s + b, 0) / last20.length : 0;
  for (let i = 1; i < candles.length - 1; i++) {
    const c = candles[i]!;
    const cn = candles[i + 1]!;
    const open = num(c[1]);
    const close = num(c[4]);
    const nextOpen = num(cn[1]);
    const nextClose = num(cn[4]);
    const nextBody = Math.abs(nextClose - nextOpen);
    if (nextBody < avgBody * 1.5) continue;
    const isBullOB = close < open && nextClose > nextOpen;
    const isBearOB = close > open && nextClose < nextOpen;
    if (isBullOB) {
      obs.push({ type: 'BULLISH', high: Math.max(open, close), low: Math.min(open, close), time: c[0]! });
    }
    if (isBearOB) {
      obs.push({ type: 'BEARISH', high: Math.max(open, close), low: Math.min(open, close), time: c[0]! });
    }
  }
  return obs.slice(-4);
}

/**
 * Erkennt Fair-Value-Gaps (3-Kerzen-Pattern: c1High < c3Low → BULLISH; c1Low > c3High → BEARISH).
 * `mitigated` ist true wenn der aktuelle Preis im Gap-Bereich liegt.
 */
export function detectFVGs(candles: KlineRow[], currentPrice: number): FairValueGap[] {
  const fvgs: FairValueGap[] = [];
  for (let i = 2; i < candles.length; i++) {
    const c1 = candles[i - 2]!;
    const c3 = candles[i]!;
    const c1High = num(c1[2]);
    const c1Low = num(c1[3]);
    const c3High = num(c3[2]);
    const c3Low = num(c3[3]);
    if (c3Low > c1High) {
      const mitigated = currentPrice <= c3Low && currentPrice >= c1High;
      fvgs.push({ type: 'BULLISH', high: c3Low, low: c1High, mitigated, time: c3[0]! });
    } else if (c1Low > c3High) {
      const mitigated = currentPrice >= c3High && currentPrice <= c1Low;
      fvgs.push({ type: 'BEARISH', high: c1Low, low: c3High, mitigated, time: c3[0]! });
    }
  }
  return fvgs.slice(-4);
}

/**
 * Yahoo Finance Chart-Response → Binance-kompatibles Klines-Array.
 * Filtert null-Kerzen (Markt geschlossen) raus.
 */
export interface YahooChartResponse {
  chart?: {
    result?: Array<{
      meta?: {
        regularMarketPrice?: number;
        previousClose?: number;
        chartPreviousClose?: number;
      };
      timestamp?: number[];
      indicators?: {
        quote?: Array<{
          open?: (number | null)[];
          high?: (number | null)[];
          low?: (number | null)[];
          close?: (number | null)[];
          volume?: (number | null)[];
        }>;
      };
    }>;
  };
}

export function yahooCandlesToBinance(chartData: YahooChartResponse | null | undefined): KlineRow[] | null {
  const result = chartData?.chart?.result?.[0];
  if (!result) return null;
  const ts = result.timestamp;
  const q = result.indicators?.quote?.[0];
  if (!ts || ts.length === 0 || !q) return null;
  const rows: KlineRow[] = [];
  for (let i = 0; i < ts.length; i++) {
    const open = q.open?.[i];
    const close = q.close?.[i];
    if (open == null || close == null) continue;
    rows.push([
      ts[i]! * 1000,
      open,
      q.high?.[i] ?? open,
      q.low?.[i] ?? open,
      close,
      q.volume?.[i] ?? 0,
    ]);
  }
  return rows.length > 0 ? rows : null;
}

/**
 * Formatiert einen Timestamp als "MM-DD HH:MM" (V1-Konvention für Chart-Tooltips).
 */
export function formatKlineTime(t: number | string): string {
  return new Date(typeof t === 'string' ? parseInt(t, 10) : t)
    .toISOString()
    .slice(5, 16)
    .replace('T', ' ');
}

/**
 * Vollanalyse einer Kline-Sequenz. V1-Verhalten:
 * - <10 Kerzen → null (zu wenig Daten)
 * - Liefert trend, swings, fib (wenn beide letzten swings existieren), OBs, FVGs
 * - Times in allen Output-Strukturen als "MM-DD HH:MM"
 */
export function analyzeKlines(
  candles: KlineRow[] | null | undefined,
  currentPrice: number,
): KlinesAnalysis | null {
  if (!Array.isArray(candles) || candles.length < 10) return null;
  const swings = detectSwings(candles);
  const trend = calcTrend(swings);
  const lastHigh = swings.highs[swings.highs.length - 1];
  const lastLow = swings.lows[swings.lows.length - 1];

  let fib: FibLevels | null = null;
  let currentZone: KlinesAnalysis['currentZone'] = null;
  let oteZone: KlinesAnalysis['oteZone'] = null;
  if (lastHigh && lastLow) {
    fib = calcFib(lastLow.price, lastHigh.price);
    currentZone = currentPrice > fib.p500 ? 'PREMIUM' : 'DISCOUNT';
    oteZone = [fib.p618, fib.p786];
  }

  const fmtPoint = (s: SwingPoint): SwingPoint => ({ price: s.price, time: formatKlineTime(s.time) });

  return {
    trend,
    currentPrice,
    lastSwingHigh: lastHigh ? fmtPoint(lastHigh) : null,
    lastSwingLow: lastLow ? fmtPoint(lastLow) : null,
    recentHighs: swings.highs.slice(-3).map(fmtPoint),
    recentLows: swings.lows.slice(-3).map(fmtPoint),
    fib,
    currentZone,
    oteZone,
    obs: detectOBs(candles).map((o) => ({ ...o, time: formatKlineTime(o.time) })),
    fvgs: detectFVGs(candles, currentPrice).map((f) => ({ ...f, time: formatKlineTime(f.time) })),
  };
}
